/**
 * Shared bell-notification dismissal helpers (HERA-9010).
 *
 * Replaces three duplicated Messenger sweeps (MessagesList, MessengerListItem,
 * Notifications) that fetched every unread row with its full payload and then
 * awaited one updateUserNotification per row. Writes go out in parallel
 * batches — the template from HERA-8498's dismissAll().
 *
 * Writes intentionally stay on AppSync mutations: the bell badge decrements
 * off the onUpdateUserNotificationByUserId subscription, which only fires for
 * mutations that go through AppSync.
 */
import { mixinMethods } from '@/main'
import { updateUserNotification, updateNotification } from '@/store/mutations'
import { unreadUserNotificationsForDismissal } from '@/store/queries'
import { HERA_MESSENGER_NEW_MESSAGE } from '@/utilities/constants/notificationTypes'
import { parseNotificationPayload } from '@/utilities/notificationHelpers'

export const DISMISS_BATCH_SIZE = 25

/**
 * Mark notifications read, at most DISMISS_BATCH_SIZE mutations in flight.
 * UserNotification rows (have userId) flip isRead; legacy Notification rows flip isReadS.
 * @param {Array<{id: string, userId?: string}>} notifications
 */
export async function markNotificationsReadInBatches(notifications, api = mixinMethods.api) {
    for (let i = 0; i < notifications.length; i += DISMISS_BATCH_SIZE) {
        const batch = notifications.slice(i, i + DISMISS_BATCH_SIZE)
        await Promise.allSettled(batch.map((notification) => {
            if (notification.userId) {
                return api(updateUserNotification, { input: { isRead: 'true', id: notification.id } })
            }
            return api(updateNotification, { input: { isReadS: 'true', id: notification.id } })
        }))
    }
}

/**
 * True when the notification is a Messenger "new message" for the given staff/device thread.
 */
export function isMessengerNotificationForThread(notification, staffId) {
    if (!staffId || notification?.notificationType !== HERA_MESSENGER_NEW_MESSAGE) return false
    return parseNotificationPayload(notification).params?.staffId === staffId
}

/**
 * Load the user's unread UserNotifications with the lean projection.
 * @param {string} userId
 * @param {object} [filter] optional ModelUserNotificationFilterInput
 */
export function loadUnreadUserNotificationsForDismissal(userId, filter, loadAll = mixinMethods.gLoadListAll) {
    const input = { userId, isRead: { eq: 'false' } }
    if (filter) input.filter = filter
    return loadAll(unreadUserNotificationsForDismissal, input, 'userNotificationsByUserIdAndIsRead')
}

/**
 * Find the user's unread Messenger notifications for one thread. The server-side
 * filter narrows by type and by the staffId appearing in the payload, so only
 * candidate rows come back; the exact payload match is confirmed client-side.
 */
export async function findUnreadMessengerNotificationsForThread(userId, staffId, loadAll = mixinMethods.gLoadListAll) {
    if (!userId || !staffId) return []
    const candidates = await loadUnreadUserNotificationsForDismissal(userId, {
        notificationType: { eq: HERA_MESSENGER_NEW_MESSAGE },
        payload: { contains: staffId }
    }, loadAll)
    return candidates.filter(n => isMessengerNotificationForThread(n, staffId))
}

// One sweep per user/thread at a time; concurrent callers share the in-flight promise.
const inFlightSweeps = new Map()

/**
 * Dismiss every unread bell notification for a Messenger thread.
 * @returns {Promise<Array>} the notifications that were dismissed
 */
export function dismissMessengerNotificationsForThread(userId, staffId, { api = mixinMethods.api, loadAll = mixinMethods.gLoadListAll } = {}) {
    if (!userId || !staffId) return Promise.resolve([])
    const key = `${userId}:${staffId}`
    if (inFlightSweeps.has(key)) return inFlightSweeps.get(key)

    const sweep = (async () => {
        try {
            const matches = await findUnreadMessengerNotificationsForThread(userId, staffId, loadAll)
            await markNotificationsReadInBatches(matches, api)
            return matches
        } catch (e) {
            console.error('Failed to dismiss messenger bell notifications:', e)
            return []
        } finally {
            inFlightSweeps.delete(key)
        }
    })()
    inFlightSweeps.set(key, sweep)
    return sweep
}
